// Frontend Configuration for Node.js Backend Integration
// Update these URLs to point to your Node.js backend server

// API Configuration
export const API_CONFIG = {
  BASE_URL: 'https://api.sudhasacademy.com/api', // Backend running on api.sudhasacademy.com
  TIMEOUT: 30000, // 30 seconds
}

// API Endpoints - Update these to match your Node.js backend routes
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
  },
  
  // User Management
  USER: {
    PROFILE: '/user/profile',
    SETTINGS: '/user/settings',
    DASHBOARD_STATS: '/user/dashboard/stats',
  },
  
  // Courses
  COURSES: {
    LIST: '/courses',
    DETAIL: (id: string) => `/courses/${id}`,
    ENROLLED: '/courses/enrolled',
    COMPLETE: '/courses/complete',
  },
  
  // Enrollments
  ENROLLMENTS: {
    LIST: '/enrollments',
    CREATE: '/enrollments',
    COMPLETE: (id: string) => `/enrollments/${id}/complete`,
  },
  
  // Cart
  CART: {
    LIST: '/cart',
    ADD: '/cart',
    REMOVE: '/cart',
    CHECKOUT: '/cart/checkout',
  },
  
  // Live Sessions
  LIVE_SESSIONS: {
    LIST: '/live-sessions',
    ACTIVE: '/live-sessions?active=true',
  },
  
  // Certificates
  CERTIFICATES: {
    LIST: '/certificates',
    DOWNLOAD: (id: string) => `/certificates/${id}/download`,
  },
  
  // Goals
  GOALS: {
    GET: '/goals',
    UPDATE: '/goals',
  },
  
  // Notifications
  NOTIFICATIONS: {
    LIST: '/notifications',
    MARK_READ: '/notifications/read',
  },
  
  // Payments
  PAYMENTS: {
    CREATE: '/payments/create',
    VERIFY: '/payments/verify',
    SUCCESS: '/payments/success',
    HISTORY: '/payments/history',
  },
  
  // Admin Routes
  ADMIN: {
    STATS: '/admin/stats',
    USERS: '/admin/users',
    COURSES: '/admin/courses',
    COURSE_DETAIL: (id: string) => `/admin/courses/${id}`,
    CATEGORIES: '/admin/categories',
    CATEGORY_DETAIL: (id: string) => `/admin/categories/${id}`,
    ANALYTICS: {
      USERS: '/admin/analytics/users',
      ENROLLMENTS: '/admin/analytics/enrollments',
      REVENUE: '/admin/analytics/revenue',
    },
    REPORTS: '/admin/reports/generate',
    NOTIFICATIONS: '/admin/notifications/send',
  },
  
  // File Uploads
  UPLOAD: {
    IMAGE: '/upload/image',
    VIDEO: '/upload/video',
  },
  
  // Gallery
  GALLERY: {
    MANIFEST: '/gallery-manifest',
  },
}

// Helper function to build full API URL
export const buildApiUrl = (endpoint: string): string => {
  return `${API_CONFIG.BASE_URL}${endpoint}`
}

// Development mode helper
export const isDevMode = (): boolean => {
  return import.meta.env.VITE_DEV_MODE === 'true'
}

// Default headers for API requests
export const getDefaultHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  }
  
  // Add auth token if available
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('token')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }
  
  return headers
}

// API request wrapper with error handling
export const apiRequest = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const url = buildApiUrl(endpoint)
  
  const config: RequestInit = {
    ...options,
    headers: {
      ...getDefaultHeaders(),
      ...options.headers,
    },
  }
  
  try {
    const response = await fetch(url, config)
    
    // Handle authentication errors
    if (response.status === 401) {
      // Clear invalid token
      if (typeof window !== 'undefined') {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        window.location.href = '/user/login'
      }
    }
    
    return response
  } catch (error) {
    console.error('API Request Error:', error)
    throw error
  }
}

// Helper functions for common API operations
export const apiGet = (endpoint: string) => apiRequest(endpoint, { method: 'GET' })
export const apiPost = (endpoint: string, data?: any) => 
  apiRequest(endpoint, { 
    method: 'POST', 
    body: data ? JSON.stringify(data) : undefined 
  })
export const apiPut = (endpoint: string, data?: any) => 
  apiRequest(endpoint, { 
    method: 'PUT', 
    body: data ? JSON.stringify(data) : undefined 
  })
export const apiDelete = (endpoint: string) => apiRequest(endpoint, { method: 'DELETE' })
